import React, { useState, useEffect } from 'react';
import { PasslyCheckbox, PasslyTable } from '@app/components';
import { vaultGroupsService, basicService, vaultsService } from '@app/services';
import { infiniteScrollItemsFetchLimit, replaceDynamicTranslation } from '@app/helpers';
import { Order, OrderType, IVaultGroup, ITableHead, IDotMenu } from '@app/models';
import { PasslyTooltip } from '@app/componentsV2';
import { confirm } from '@app/components/Modal/confirmModal';
import { useTypedTranslation } from '@app/hooks';
import { useDispatch } from 'react-redux';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { resetScroll } from '@app/store/actions';
import { TableCell } from '@material-ui/core';

interface IProps {
    vaultId: string;
    groupsToAdd: string[];
}

interface IVaultGroupRow extends IVaultGroup {
    checked: boolean;
    [propName: string]: any;
}

enum TableHeadEnum {
    displayName = 'DisplayName',
    isAdmin = 'IsAdmin',
    canAdd = 'CanAdd',
    canUpdate = 'CanUpdate',
    canDelete = 'CanDelete',
    canRead = 'CanRead',
    canLaunch = 'CanLaunch'
}

const VaultAccess: React.SFC<IProps> = ({ vaultId, groupsToAdd }) => {
    const dispatch = useDispatch();

    const [rows, setRows] = useState<IVaultGroupRow[]>([]);
    const [groupsCount, setGroupsCount] = useState(0);
    const [reset, setReset] = useState(0);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const { translation } = useTypedTranslation();

    useEffect(() => {
        if (!initialLoad) {
            resetData();
        }
    }, [order]);

    useEffect(() => {
        if (groupsToAdd.length > 0) {
            vaultGroupsService.addVaultGroups(groupsToAdd, vaultId, handleAddGroupSuccess, handleGenericApiFailure);
        }
    }, [groupsToAdd]);

    const resetData = () => {
        setGroupsCount(0);
        setRows([]);
        setLoading(true);
        setReset(reset + 1);
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        setInitialLoad(false);
        if (isReset) {
            setLoading(true);
        }

        vaultGroupsService
            .getVaultGroups(vaultId, 'DisplayName', order, infiniteScrollItemsFetchLimit, skipCount, isReset)
            .then((response: any) => {
                const count = response.data['@odata.count'];

                if (count !== undefined) {
                    setGroupsCount(count);
                }

                const groups: IVaultGroupRow[] = response.data.value;

                groups.forEach((group) => {
                    group.checked = false;
                    group.id = group.Id;
                });

                if (groups.length === 0) {
                    setRows([]);
                } else if (count !== undefined) {
                    setRows(groups);
                } else {
                    setRows(rows.concat(groups));
                }

                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const handleAddGroupSuccess = () => {
        vaultsService.updateVaultsPermissions();
        resetData();
    };

    const onPermissionCheckboxChange = (index: number, name: string) => (checked: boolean) => {
        let updatedRow = rows[index];
        let names = [name];
        updatedRow[name] = checked;

        const updateObject = {
            VaultGroup: {
                Id: updatedRow.Id,
                IsAdmin: updatedRow.IsAdmin,
                CanRead: updatedRow.CanRead,
                CanAdd: updatedRow.CanAdd,
                CanUpdate: updatedRow.CanUpdate,
                CanDelete: updatedRow.CanDelete,
                CanLaunch: updatedRow.CanLaunch
            }
        };

        if (name === 'CanAdd') {
            if (checked) {
                updateObject.VaultGroup.CanUpdate = checked;
                updateObject.VaultGroup.CanRead = checked;
                updateObject.VaultGroup.CanLaunch = checked;
                names = ['CanAdd', 'CanUpdate', 'CanRead', 'CanLaunch'];
            } else {
                updateObject.VaultGroup.CanUpdate = checked;
                names = ['CanAdd', 'CanUpdate'];
            }
        }

        if (name === 'CanUpdate') {
            if (checked) {
                updateObject.VaultGroup.CanRead = checked;
                updateObject.VaultGroup.CanLaunch = checked;
                names = ['CanUpdate', 'CanRead', 'CanLaunch'];
            } else {
                names = ['CanUpdate'];
            }
        }

        vaultGroupsService.updateVaultGroupPermissions(
            updateObject,
            vaultId,
            handlePermissionChangeSuccess(checked, index, names),
            handleGenericApiFailure
        );
    };

    const handlePermissionChangeSuccess = (checked: boolean, index: number, names: string[]) => () => {
        vaultsService.updateVaultsPermissions();
        let newRows = [...rows];
        names.forEach((name) => {
            newRows[index][name] = checked;
        });

        setRows(newRows);
    };

    const handleGenericApiFailure = () => {
        dispatch(handleSetSnackbar({ show: true, message: translation('GENERIC_ERROR'), severity: 'error', dismissOnly: true }));
    };

    const goToGroup = (row: IVaultGroupRow) => () => {
        basicService.redirect(`/directory/groups/details/${row.GroupId}`, true);
    };

    const removeGroupsDialog = (ids: string[]) => {
        const singular: boolean = ids.length === 1;

        confirm(
            translation(singular ? 'VAULT_REMOVE_GROUP_BODY' : 'VAULT_REMOVE_GROUPS_BODY'),
            translation('REMOVE'),
            translation('CANCEL'),
            translation(singular ? 'VAULT_REMOVE_GROUP_HEADER' : 'VAULT_REMOVE_GROUPS_HEADER'),
            false,
            { isDeleteAction: true }
        ).then(
            () => {
                return removeGroups(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const removeGroups = (ids: string[]) => {
        vaultGroupsService.removeVaultGroups(ids, vaultId, handleRemoveGroupsSuccess.bind(null, ids), handleGenericApiFailure);
        return true;
    };

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        removeGroupsDialog(ids);
    };

    const deleteSelected = () => {
        let ids: string[] = [];

        rows.forEach((row) => {
            if (row.checked) {
                ids.push(row.Id);
            }
        });

        removeGroupsDialog(ids);
    };

    const handleRemoveGroupsSuccess = (ids: string[]) => {
        let filteredRows = rows.filter((row) => {
            return !ids.includes(row.Id);
        });

        const snackbarMessage = `${
            ids.length === 1
                ? replaceDynamicTranslation(translation('GENERIC_REMOVE_SUCCESS_SINGLE'), translation('GROUP'))
                : ids.length.toString() + ' ' + replaceDynamicTranslation(translation('GENERIC_REMOVE_SUCCESS_PLURAL'), translation('GROUPS'))
        }`.toLocaleLowerCase();

        dispatch(handleSetSnackbar({ show: true, message: snackbarMessage, severity: 'success' }));
        setGroupsCount(groupsCount - ids.length);
        setRows(filteredRows);
        vaultsService.updateVaultsPermissions();
    };

    const getPermissionCell = (group: IVaultGroupRow, index: number, name: string) => {
        const isAdminCheckbox = name === 'IsAdmin';
        const isCanAddCheckbox = name === 'CanAdd';
        const isCanDeleteCheckbox = name === 'CanDelete';
        const isCanUpdateCheckbox = name === 'CanUpdate';
        const isCanAddChecked = group.CanAdd && !isAdminCheckbox && !isCanAddCheckbox && !isCanDeleteCheckbox;
        const isCanUpdateChecked = group.CanUpdate && !isAdminCheckbox && !isCanAddCheckbox && !isCanUpdateCheckbox && !isCanDeleteCheckbox;
        const isReadOrLaunchDisabled = (name === 'CanLaunch' && !group.CanRead) || (name === 'CanRead' && !group.CanLaunch);

        const isDisabled = isCanAddChecked || isCanUpdateChecked || isReadOrLaunchDisabled || (!isAdminCheckbox && group.IsAdmin);

        return (
            <>
                <PasslyTooltip title={isReadOrLaunchDisabled ? translation('VAULT_PERMISSIONS_READ_LAUNCH_TOOLTIP') : ''}>
                    <TableCell align='left' size='small'>
                        <PasslyCheckbox
                            name='select'
                            checked={group[name] || group.IsAdmin}
                            onChange={onPermissionCheckboxChange(index, name)}
                            label=''
                            disabled={isDisabled}
                        />
                    </TableCell>
                </PasslyTooltip>
            </>
        );
    };

    const createSortHandler = () => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;

        setOrder(ascDesc);
        dispatch(resetScroll());
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.displayName,
            label: 'GROUPS_NAME'
        },
        {
            id: TableHeadEnum.isAdmin,
            headerTooltipText: 'ADMIN_PERMISSION_TOOLTIP',
            label: 'VAULT_ADMIN',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        },
        {
            id: TableHeadEnum.canAdd,
            headerTooltipText: 'ADD_PERMISSION_TOOLTIP',
            label: 'VAULT_ADD',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        },
        {
            id: TableHeadEnum.canUpdate,
            headerTooltipText: 'UPDATE_PERMISSION_TOOLTIP',
            label: 'VAULT_UPDATE',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        },
        {
            id: TableHeadEnum.canDelete,
            headerTooltipText: 'DELETE_PERMISSION_TOOLTIP',
            label: 'VAULT_DELETE',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        },
        {
            id: TableHeadEnum.canRead,
            headerTooltipText: 'READ_PERMISSION_TOOLTIP',
            label: 'VAULT_READ',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        },
        {
            id: TableHeadEnum.canLaunch,
            headerTooltipText: 'LAUNCH_PERMISSION_TOOLTIP',
            label: 'VAULT_LAUNCH',
            type: 'customCheckbox',
            hideSorting: true,
            getCustomCheckboxCell: getPermissionCell
        }
    ];

    return (
        <>
            <PasslyTable
                rows={rows}
                setRows={setRows}
                order={order}
                orderBy={'DisplayName'}
                handleOrderChange={createSortHandler}
                headerItems={headCells}
                rowsCount={groupsCount}
                onFetch={fetchMoreData}
                withCheckboxes={true}
                emptyListHelper={translation('VAULT_PASSWORDS_EMPTY_LIST')}
                loadingData={loading}
                resetLocal={reset}
                openInNewTab={goToGroup}
                deleteRow={deleteRow}
                actionMenuItems={actionMenuItems}
                hideFilter={true}
            />
        </>
    );
};

export default VaultAccess;
